import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { QUERY_KEYS } from "../constants";
import {
  IProjectTeamMember,
  IProjectTeamMemberPayload,
  IProjectTeamSyncPayload,
  IProjectTeamSyncResult,
  IUpdateProjectTeamMemberPayload
} from "../types";

import { AZURE_MJ } from "@/lib/axios";

export const useGetAvailableRoles = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.availableRoles],
    queryFn: async (): Promise<string[]> => {
      const { data } = await AZURE_MJ.get("v3/project-team/roles");
      return data;
    }
  });
};

// Shared by UserContext (on app load) and ProjectTeamPage. The staleTime lets
// the page reuse the cached list instead of refetching on mount; the mutations
// below invalidate it, so edits still show up immediately.
export const useGetProjectTeamMembers = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.allTeamMembersList],
    queryFn: async (): Promise<IProjectTeamMember[]> => {
      const { data } = await AZURE_MJ.get("v3/project-team/all");
      return data;
    },
    staleTime: 5 * 60 * 1000
  });
};

// Syncs all Azure AD users into the project-team database. Newly-introduced
// users come back from `GET v3/project-team/all` with `IsNewFromSync: true`, so
// on success we just invalidate that list and let the table re-render.
export const useSyncProjectTeamUsers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: IProjectTeamSyncPayload
    ): Promise<IProjectTeamSyncResult> => {
      const { data } = await AZURE_MJ.post("v3/project-team/sync", payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.allTeamMembersList]
      });
    }
  });
};

export const useCreateProjectTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: IProjectTeamMemberPayload): Promise<void> => {
      const res = await AZURE_MJ.post("v3/project-team/assign", payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.allTeamMembersList]
      });
    }
  });
};

export const useDeleteProjectTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      const res = await AZURE_MJ.delete(`v3/project-team/user/${email}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.allTeamMembersList]
      });
    }
  });
};

export const useUpdateProjectTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      payload
    }: {
      email: string;
      payload: IUpdateProjectTeamMemberPayload;
    }): Promise<void> => {
      const res = await AZURE_MJ.put(`v3/project-team/user/${email}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.allTeamMembersList]
      });
    }
  });
};
